import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FieldType, FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { takeUntil, tap } from 'rxjs';
import { ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { UntilDestroy } from '@ngneat/until-destroy';
import { InfoTooltipComponent } from '../info-tooltip.component';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { FormlyErrorComponent } from '../formly-error.component';

@UntilDestroy()
@Component({
  selector: 'formly-field-date-offset',
  template: `
    <h3 class="mb-2">
      {{ props.label }}
      <app-info-tooltip [tooltip]="props.info"></app-info-tooltip>
    </h3>
    <div class="w-full">
      @for (field of field.fieldGroup; track $index) {
        <formly-field [field]="field" [class]="field.props.className"></formly-field>
      }
    </div>
    @if (field.props.description) {
      <mat-hint class="inline-block mb-2">{{ field.props.description }}</mat-hint>
    }
    <app-formly-error [field]="field"></app-formly-error>
  `,
  imports: [
    ReactiveFormsModule,
    MatAutocompleteModule,
    FormlyModule,
    MatFormFieldModule,
    MatInputModule,
    InfoTooltipComponent,
    FormlyErrorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyFieldDateOffsetComponent extends FieldType implements OnInit {
  private destroy$ = injectDestroy();
  constructor() {
    super();
  }

  ngOnInit() {
    this.field.props.hideDateOffsetType = this.field.props.hideDateOffsetType ?? false;
    if (this.field.props.hideDateOffsetType) {
      this.field.fieldGroup.forEach((field: FormlyFieldConfig) => {
        if (field.key === 'dateOffsetType') {
          field.className = 'hidden';
        }
      });
    }

    this.field.formControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          const parsedValue = parseFloat(this.field.formControl.get('dateOffsetValue')?.value);
          const value = Number.isFinite(parsedValue) ? parsedValue : 1;
          const unit = this.field.formControl.get('dateOffsetUnit')?.value ?? 'minutes';
          const units: Record<string, number> = {
            minutes: 1,
            hours: 60,
            days: 24 * 60,
            weeks: 7 * 24 * 60,
            months: 30 * 24 * 60,
            years: 365 * 24 * 60,
          };
          const minutes = value * units[unit];
          const minutesControl = this.field.formControl.get('dateOffsetInMinutes');
          // Emit the patch so Formly syncs the model (the saved payload); the equality
          // guard terminates the re-entrant valueChanges loop after one pass.
          if (Number.isFinite(minutes) && minutesControl?.value !== minutes) {
            minutesControl?.patchValue(minutes);
          }
        })
      )
      .subscribe();
  }
}
